import { useCallback, useEffect, useMemo } from 'react';

import { useGetEntryDetailsQuery, useNotifyOnSettledEntryOrPickSubscription } from '@/api/entries/query.generated';
import { useAllLeaguesUpcomingEventsQuery, useEventsInfoQuery } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import {
    getEventIdsFromPicks,
    shouldDisplayDynamicMultipliersForNonVoided,
} from '@/feature/entries-pickem/entries-utils';
import { checkIfBoostedProjection } from '@/feature/lobby/utils/lineupsUtils';
import { Result } from '@/types/api.generated';
import { gameUtils } from '@/utils/games';
import { getEntryPayout } from '@/utils/getEntryPayout';

export const useEntryDetails = (entryId: string) => {
    const [{ data, fetching }, executeGetEntryDetails] = useGetEntryDetailsQuery({
        variables: { id: entryId },
        pause: true,
    });

    const [{ data: entryData }] = useNotifyOnSettledEntryOrPickSubscription(
        { variables: { id: entryId } },
        (_, next) => next
    );

    const entry = useMemo(() => {
        if (entryData?.notifyOnSettledEntryOrPick) {
            return entryData.notifyOnSettledEntryOrPick;
        }

        if (data?.getEntry) {
            return data.getEntry;
        }
        return;
    }, [data?.getEntry, entryData?.notifyOnSettledEntryOrPick]);

    const eventIds = useMemo(() => getEventIdsFromPicks(entry?.picks), [entry]);

    const [{ data: eventsInfoData }, excuteEventsInfoQuery] = useEventsInfoQuery({
        requestPolicy: 'network-only',
        variables: {
            ids: eventIds,
        },
        pause: true,
    });

    const eventsMap: Record<string, EventInfo> = useMemo(() => {
        if (eventsInfoData?.getEventsByIdsV2?.length) {
            return eventsInfoData.getEventsByIdsV2.reduce((acc, event) => ({ ...acc, [event.id]: event }), {});
        }
        return {};
    }, [eventsInfoData?.getEventsByIdsV2]);

    const eventsEnded = useMemo(
        () => Object.values(eventsMap).every(event => gameUtils.hasEnded(event.status)),
        [eventsMap]
    );
    const eventsDataLoaded = eventsInfoData !== undefined;

    // Fetch all the upcoming events to determine if there are active events in the app for this entry
    // This is a memory efficient query, that only fetches the events and their status
    // Only fetch if the events are not settled/finished
    const [{ data: leaguesUpcomingEvents }] = useAllLeaguesUpcomingEventsQuery({
        requestPolicy: 'cache-and-network',
        pause: eventsEnded,
    });

    const entryPayout = useMemo(() => getEntryPayout({ picks: entry?.picks }), [entry]);
    const nonVoidedCount = useMemo(
        () => entry?.picks.filter(p => p.result !== Result.Void).length ?? 0,
        [entry?.picks]
    );

    const dynamicMultipliers = useMemo(() => {
        const initialMultipliers = entry?.dynamicMultipliers?.filter(it => it.numberOfPicks === entry.picks.length);
        const multipliersForNonVoided = entry?.dynamicMultipliers?.filter(it => it.numberOfPicks === nonVoidedCount);
        return shouldDisplayDynamicMultipliersForNonVoided({ entry, entryPayout })
            ? multipliersForNonVoided
            : initialMultipliers;
    }, [entry, entryPayout, nonVoidedCount]);

    const boostedPicks = useMemo(
        () => entry?.picks?.filter(pick => checkIfBoostedProjection(pick.projection.type)),
        [entry]
    );
    const nonVoidedPickCount = entry?.picks?.filter(pick => pick.result !== Result.Void).length ?? 0;
    const nonVoidedBoostedPicksCount = boostedPicks?.filter(pick => pick.result !== Result.Void).length ?? 0;
    const dynamicBoostedMultiplier = useMemo(
        () =>
            entry?.boostedMultipliers?.find(
                it => it.numberOfPicks === nonVoidedPickCount && it.numberOfBoostedPicks === nonVoidedBoostedPicksCount
            ),
        [entry, nonVoidedPickCount, nonVoidedBoostedPicksCount]
    );

    const liveEvents = useMemo(
        () => eventsInfoData?.getEventsByIdsV2?.filter(event => gameUtils.isLive(event)) ?? [],
        [eventsInfoData]
    );
    const liveEventIds = useMemo(() => liveEvents.map(event => event.id), [liveEvents]);

    useEffect(() => {
        if (!entry) {
            return;
        }
        excuteEventsInfoQuery();
    }, [entry, excuteEventsInfoQuery]);

    const reload = useCallback(() => {
        executeGetEntryDetails({ requestPolicy: 'network-only' });
    }, [executeGetEntryDetails]);

    return {
        entry,
        eventsMap,
        eventsDataLoaded,
        eventsEnded,
        leaguesUpcomingEvents,
        entryPayout,
        dynamicMultipliers,
        dynamicBoostedMultiplier,
        liveEventIds,
        reload,
        fetching,
    };
};
